/**
 * ETag / If-Match helpers for budget service line concurrency (FE-P3-05 / BE-P3-04).
 * ETag is derived from `updatedAt` as a strong quoted ISO-8601 token (mirrors BE).
 */

/** Normalize `updatedAt` to ISO-8601, or `null` when missing/invalid. */
export function normalizeUpdatedAtIso(
  updatedAt: Date | string | null | undefined,
): string | null {
  if (updatedAt == null || updatedAt === "") {
    return null;
  }

  const date =
    updatedAt instanceof Date ? updatedAt : new Date(String(updatedAt));

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

/**
 * Build an `If-Match` / batch `ifMatch` value from local `updatedAt`.
 * Returns `undefined` when unknown so the write stays last-write-wins.
 */
export function buildServiceIfMatch(
  updatedAt: Date | string | null | undefined,
): string | undefined {
  const iso = normalizeUpdatedAtIso(updatedAt);
  return iso == null ? undefined : `"${iso}"`;
}

/**
 * Parse an ETag / If-Match token into ISO `updatedAt`.
 * Accepts quoted (`"ISO"`), weak (`W/"ISO"`), or raw ISO strings.
 */
export function parseServiceETagToIso(
  etag: string | null | undefined,
): string | null {
  if (etag == null) {
    return null;
  }

  let token = String(etag).trim();
  if (!token) {
    return null;
  }

  if (/^W\//i.test(token)) {
    token = token.slice(2).trim();
  }
  if (token.startsWith('"') && token.endsWith('"') && token.length >= 2) {
    token = token.slice(1, -1);
  }

  return normalizeUpdatedAtIso(token);
}

/** Collect durable `updatedAt` values keyed by service `_id`. */
export function collectServiceUpdatedAts(
  services: Array<{
    _id?: string | null;
    updatedAt?: Date | string | null;
  }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const service of services) {
    const id = service?._id != null ? String(service._id) : "";
    if (!id) continue;
    const iso = normalizeUpdatedAtIso(service.updatedAt);
    if (iso) {
      map.set(id, iso);
    }
  }
  return map;
}

type AxiosLikeError = {
  response?: {
    status?: number;
    headers?: Record<string, string | undefined>;
    data?: {
      statusCode?: number;
      etag?: string;
      message?: string | string[];
    };
  };
};

/** True when the API rejected a conditional write (412 Precondition Failed). */
export function isServiceVersionConflictError(error: unknown): boolean {
  const err = error as AxiosLikeError | undefined;
  const status = err?.response?.status ?? err?.response?.data?.statusCode;
  return status === 412;
}

/**
 * Current ETag from a 412 response (`ETag` header or JSON `etag` body field).
 */
export function getConflictETagFromError(error: unknown): string | null {
  const err = error as AxiosLikeError | undefined;
  const headers = err?.response?.headers;
  const headerEtag =
    headers?.etag ?? headers?.ETag ?? headers?.["etag"] ?? headers?.["ETag"];
  if (headerEtag) {
    return String(headerEtag);
  }
  const bodyEtag = err?.response?.data?.etag;
  return bodyEtag != null ? String(bodyEtag) : null;
}
